import { FC, useMemo } from 'react'
import {
	AppstoreOutlined,
	DashboardOutlined,
	BlockOutlined,
	SettingOutlined,
	FileTextOutlined,
	UsergroupAddOutlined,
	HomeOutlined,
	UserAddOutlined,
	QuestionCircleOutlined,
	LogoutOutlined,
} from '@ant-design/icons'
import appRoutes from '../app-routes'
import Link from 'next/link'
import { useMainMenu } from './hooks'
import { MAIN_MENU_KEYS } from './consts'
import { useShowModal } from '../GlobalModals'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import { useCPAs, useReceiverInvites } from 'api'
import { SETTINGS_TABS } from 'modules/settings/SettingsModal/consts'
import Styled from './styled'
import { useScreenMatch } from 'theme/hooks'
import { Badge } from 'antd'
import http from 'api/http'
import { useIsWealthicaUser } from '../hooks'
import { useAppContext } from '../appContext'

interface IMenu {
	onClick?: () => void
}

const Menu: FC<IMenu> = ({ onClick }) => {
	const { t, currentMenuKey, isCPAview, user, isFetched: isUserFetched } = useMainMenu()
	const showModal = useShowModal()
	const { data: cpas = [], isFetched: areCPAsFetched } = useCPAs()
	const match = useScreenMatch()
	const { data: receivedAccessInvites = [] } = useReceiverInvites('ACCESS', {
		enabled: isUserFetched,
	})
	const { data: receivedOrganizationInvites = [] } = useReceiverInvites('ORGANIZATION', {
		enabled: !!user?.isCPA && isUserFetched,
	})
	const { isFetched, isWealthicaContext } = useAppContext()
	const isWealthicaUser = useIsWealthicaUser()

	const menuItems = useMemo(() => {
		let roleMenus: ItemType[] = []

		if (isCPAview) {
			roleMenus = roleMenus.concat([
				{
					icon: <HomeOutlined />,
					label:
						!!receivedOrganizationInvites.length && !user?.orgId ? (
							<Badge size="small" dot>
								<Link href={appRoutes.organization()}>{t('terms.organization')}</Link>
							</Badge>
						) : (
							<Link href={appRoutes.organization()}>{t('terms.organization')}</Link>
						),
					key: MAIN_MENU_KEYS.ORGANIZATION,
					onClick,
				},
				{
					icon: <UsergroupAddOutlined />,
					label: receivedAccessInvites.length ? (
						<Badge size="small" dot>
							<Link href={appRoutes.clients.list()}>{t('actions.manageClients')}</Link>
						</Badge>
					) : (
						<Link href={appRoutes.clients.list()}>{t('actions.manageClients')}</Link>
					),
					key: MAIN_MENU_KEYS.CLIENTS,
					onClick,
				},
			])
		} else {
			roleMenus = roleMenus.concat([
				{
					icon: <DashboardOutlined />,
					label: <Link href={appRoutes.dashboard()}>{t('terms.dashboard')}</Link>,
					key: MAIN_MENU_KEYS.DASHBOARD,
					onClick,
				},
				{
					icon: <AppstoreOutlined />,
					label: <Link href={appRoutes.integrations.list()}>{t('terms.wallets')}</Link>,
					key: MAIN_MENU_KEYS.WALLETS,
					onClick,
				},
				{
					icon: <BlockOutlined />,
					label: <Link href={appRoutes.transactions.list()}>{t('terms.transactions')}</Link>,
					key: MAIN_MENU_KEYS.TRANSACTIONS,
					onClick,
				},
				{
					icon: <FileTextOutlined />,
					label: <Link href={appRoutes.reports.list()}>{t('terms.taxReports')}</Link>,
					key: MAIN_MENU_KEYS.REPORTS,
					onClick,
				},
			])
		}

		roleMenus = roleMenus.concat([
			{
				type: 'divider',
				key: 'divider',
			},
			{
				icon: <SettingOutlined />,
				label:
					!!receivedAccessInvites.length && !user?.isCPA ? (
						<Badge size="small" dot>
							{t('terms.settings')}
						</Badge>
					) : (
						t('terms.settings')
					),
				key: MAIN_MENU_KEYS.SETTINGS,
				onClick: () => {
					onClick?.()
					showModal('settings')
				},
			},
		])

		if (!match.gte('md') && !isWealthicaUser) {
			roleMenus = roleMenus.concat([
				{
					icon: <QuestionCircleOutlined />,
					label: t('terms.help'),
					key: MAIN_MENU_KEYS.HELP,
					onClick: () => {
						zE('messenger:set', 'cookies', true)
						zE('messenger', 'open')
					},
				},
			])
		}

		if (areCPAsFetched && cpas.length === 0 && !isCPAview) {
			roleMenus = roleMenus.concat([
				{
					icon: <UserAddOutlined />,
					label: t('actions.inviteSpecialist'),
					className: '__remarkable',
					key: MAIN_MENU_KEYS.INVITE_CPA,
					onClick: () => {
						onClick?.()
						showModal('settings', { initialTab: SETTINGS_TABS.ACCESS })
					},
				},
			])
		}

		if (isFetched && !isWealthicaContext) {
			roleMenus = roleMenus.concat([
				{
					icon: <LogoutOutlined />,
					label: t('actions.logout'),
					key: MAIN_MENU_KEYS.LOGOUT,
					onClick: () => {
						http.logout()
					},
				},
			])
		}

		return roleMenus
	}, [
		t,
		showModal,
		isCPAview,
		cpas,
		match,
		receivedAccessInvites,
		receivedOrganizationInvites,
		areCPAsFetched,
		isWealthicaUser,
		isFetched,
		isWealthicaContext,
	])

	return <Styled.Menu selectedKeys={currentMenuKey ? [currentMenuKey] : undefined} items={menuItems} />
}

export default Menu
